import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { Icon, FormControlLabel } from '@mui/material';
import { FlexGridCell, FlexGridList, FlexGridRow } from 'global/components/flex-grid-list';
import Row from './row';
import {
  AlertsContainer,
  AlertsLocationContainer,
  AlertsLocationContent,
} from './styles';
import { get, size } from 'global/lib/lodash';
import { useSelector, useDispatch } from 'react-redux';
import Switch from 'global/components/switch';
import { clearNewAlerts, getAlerts, getAllAlerts } from 'redux/actions/view';
import setFilter from 'redux/actions/setFilter';

const AlertsFlexList = (props) => <FlexGridList zebra={false} zebraMode="prop" {...props} />;

const TableHeader = (props) => {
  return (
    <FlexGridRow layout="columns" dense header hover clickable px={1}>
      <FlexGridCell
        width="85%"
        alignItems="stretch"
        sx={{ pr: 1, flexDirection: { xs: 'column', md: 'row' } }}
      >
        <FlexGridCell width="20%" hideBelow="md" sx={{ mt: 0.5 }}>
          Type
        </FlexGridCell>
        {props.showLocation && (
          <FlexGridCell width="25%" hideBelow="md" sx={{ mt: 0.5, mr: { xs: 0, md: 1 } }}>
            Location
          </FlexGridCell>
        )}
        <FlexGridCell flexGrow={1} stackAt="md" sx={{ mt: 0.5 }}>
          Description
        </FlexGridCell>
      </FlexGridCell>
      <FlexGridCell width="15%">Time</FlexGridCell>
    </FlexGridRow>
  );
};

// const pageSize = 25;
export default forwardRef((props, ref) => {
  const page = get(props, 'match.path') === '/alerts/user' ? 'user' : 'all';

  const hasRealm = useSelector((state) => get(state, 'auth.realm'));
  const alerts = useSelector((state) => get(state, `view.alerts.${page}`));
  const newAlerts = useSelector(state => get(state, 'view.alerts.new'));
  const filter = useSelector((state) => get(state, `filter.alerts.${page}`));
  const { locationOn } = filter;

  const [showLocation] = useState(page === 'user' ? false : true);
  const [data, setData] = useState(alerts);
  const [origRealm] = useState(hasRealm);
  const [loading, setLoading] = useState(false);
  const [paginate, setPaginate] = useState(1);

  const showRealmFilter = get(props, 'showRealmFilter', true);

  const dispatch = useDispatch();

  const clearAll = get(props, 'clearAll', true);

  const getPageAlerts = async () => {
    try {
      setLoading(true);
      const method = page === 'all' ? getAllAlerts : getAlerts;
      await dispatch(method(paginate));
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const filterAlerts = () => {
    if (locationOn && hasRealm && showRealmFilter) {
      return alerts.filter((a) => get(a, 'realms.0.id') == hasRealm.id);
    } else {
      return alerts;
    }
  };

  useEffect(() => {
    getPageAlerts();
  }, [paginate]);

  useEffect(() => {
    setPaginate(1);
  }, [page]);

  useEffect(() => {
    setData(filterAlerts());
  }, [alerts]);

  useEffect(() => {
    if (clearAll && size(newAlerts)) {
      dispatch(clearNewAlerts());
    }
  }, [newAlerts]);

  const setLocationOn = (value) => {
    dispatch(setFilter('alerts', page, { ...filter, locationOn: value }));
  };

  useEffect(() => {
    if (!hasRealm) {
      setLocationOn(false);
    } else if (hasRealm && hasRealm.id !== get(origRealm, 'id')) {
      setLocationOn(true);
    }
  }, [hasRealm]);

  useEffect(() => {
    setData(filterAlerts());
  }, [locationOn, hasRealm]);

  const locationText = () => {
    if (locationOn) {
      return (
        <span>
          Showing only alerts from <b>{hasRealm.name}</b> based on your current location.
        </span>
      );
    } else {
      return `Showing all alerts, even though your current location is set to ${hasRealm.name}.`;
    }
  };

  const handleLocation = (e) => {
    setLocationOn(e.target.checked);
  };

  const updatePage = (newPage) => {
    if (newPage > paginate) {
      setPaginate(newPage);
    }
  };

  useImperativeHandle(ref, () => ({
    getPageAlerts,
  }), []);

  return (
    <AlertsContainer>
      {hasRealm && showRealmFilter && (
        <AlertsLocationContainer locationOn={!!locationOn}>
          <AlertsLocationContent>
            <p>{locationText()}</p>
            <FormControlLabel control={<Switch checked={!!get(filter, 'locationOn')} onChange={handleLocation} />} label="USE LOCATION" />
          </AlertsLocationContent>
          <Icon>place</Icon>
        </AlertsLocationContainer>
      )}

      <ListWithPages
        {...props}
        header={<TableHeader showLocation={showLocation} />}
        data={data}
        loading={loading}
        showLocation={showLocation}
        handlePageChange={updatePage}
        alwaysReplaceData
        currentPage={paginate}
      />
    </AlertsContainer>
  );
});

const ListWithPages = withInfiniteScroll(Row, AlertsFlexList);
